import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  IconCheck,
  IconLoader2,
  IconPlus,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { Button, CircleLoader, Message } from "../../components";
import { useDebouncedValue } from "../../../hooks";
import { searchSongs, type SongResult } from "../../../services";
import { SongArt } from "./SongArt";

interface SearchPanelProps {
  /** Adds a song; resolves true when it is now in the queue (show ✓). */
  onAdd: (song: SongResult) => Promise<boolean>;
}

const LIST_ID = "dj-results-list";

export const SearchPanel = ({ onAdd }: SearchPanelProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query.trim(), 400);
  const [results, setResults] = useState<SongResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [addedKeys, setAddedKeys] = useState<Set<string>>(new Set());
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inFlight = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (debounced.length < 2) {
      setResults([]);
      setError(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    searchSongs(debounced)
      .then((r) => {
        if (!cancelled) setResults(r);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  // Reset the keyboard highlight whenever the result set changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  // Close the popover on a click outside the search.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const trimmed = query.trim();
  const panelOpen = open && trimmed.length >= 2;
  const showEmpty =
    !loading && !error && debounced.length >= 2 && results.length === 0;

  const handleChange = (value: string) => {
    setQuery(value);
    setActiveIndex(-1);
    if (value.trim().length >= 2) {
      setOpen(true);
      setLoading(true);
    } else {
      setOpen(false);
      setResults([]);
      setError(false);
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setError(false);
    setLoading(false);
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleAdd = async (song: SongResult) => {
    // Guard synchronously: the disabled attribute only lands after a re-render,
    // so a fast double-tap could otherwise fire two requests for the same song.
    if (inFlight.current.has(song.mbid) || addedKeys.has(song.mbid)) return;
    inFlight.current.add(song.mbid);
    setBusyKeys((prev) => new Set(prev).add(song.mbid));
    try {
      const added = await onAdd(song);
      if (added) {
        setAddedKeys((prev) => {
          const next = new Set(prev);
          next.add(song.mbid);
          return next;
        });
      }
    } finally {
      inFlight.current.delete(song.mbid);
      setBusyKeys((prev) => {
        const next = new Set(prev);
        next.delete(song.mbid);
        return next;
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!panelOpen || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleAdd(results[activeIndex]);
    }
  };

  return (
    <div className="dj-search" ref={containerRef}>
      <div className="dj-search-field">
        <IconSearch className="dj-search-icon" aria-hidden="true" />
        <input
          ref={inputRef}
          className="dj-search-input"
          type="search"
          role="combobox"
          aria-expanded={panelOpen}
          aria-controls={LIST_ID}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `dj-result-${activeIndex}` : undefined
          }
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => {
            if (trimmed.length >= 2) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={t("dj.searchPlaceholder")}
          aria-label={t("dj.searchPlaceholder")}
        />
        {query && (
          <button
            type="button"
            className="dj-search-clear"
            onClick={handleClear}
            aria-label={t("dj.clearSearch")}
          >
            <IconX />
          </button>
        )}
      </div>

      {panelOpen && (
        <div className="dj-results-panel">
          {loading && (
            <div className="dj-search-loading">
              <CircleLoader size="small" />
            </div>
          )}
          {!loading && error && (
            <Message variant="error">{t("dj.searchError")}</Message>
          )}
          {showEmpty && (
            <Message variant="primary">{t("dj.noResults")}</Message>
          )}
          {!loading && !error && results.length > 0 && (
            <ul className="dj-results" id={LIST_ID} role="listbox">
              {results.map((song, idx) => {
                const added = addedKeys.has(song.mbid);
                const busy = busyKeys.has(song.mbid);
                return (
                  <li
                    key={song.mbid}
                    id={`dj-result-${idx}`}
                    role="option"
                    aria-selected={idx === activeIndex}
                    className={clsx("dj-result", {
                      active: idx === activeIndex,
                      added,
                    })}
                  >
                    <SongArt coverArt={song.coverArt} />
                    <div className="dj-result-info">
                      <span className="dj-result-title">{song.title}</span>
                      <span className="dj-result-artist">
                        {song.artist}
                        {song.year ? ` · ${song.year}` : ""}
                      </span>
                    </div>
                    {added ? (
                      <Button
                        variant="success"
                        appearance="muted"
                        icon={<IconCheck />}
                        iconOnly
                        disabled
                        aria-label={t("dj.added")}
                      />
                    ) : (
                      <Button
                        variant="primary"
                        appearance="muted"
                        icon={
                          busy ? (
                            <IconLoader2 className="dj-spin" />
                          ) : (
                            <IconPlus />
                          )
                        }
                        onClick={() => handleAdd(song)}
                        disabled={busy}
                      >
                        {t("dj.add")}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
